import { useState } from "react"
import { Link, router, useLocalSearchParams } from "expo-router"
import { ActivityIndicator, Modal, Pressable, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { Show, useObservable } from "@legendapp/state/react"
import { useDebounce } from "use-debounce"

import { ProductType } from "@white-labels/input/src/m5azen"

import { ar } from "@src/i18n"
import { useNumCols } from "@src/hooks"
import { formatPrice, getScaleCode, sharedStyles, trpc, ENV } from "@src/utils"
import { formatWeight } from "@white-labels/utils"
import { List, Header, Scanner, Text, TextInput, ModalWrapper, Image, MCIcon } from "@src/components"

const TITLES = {
  undefined: ar.products,
  STANDARD_PRODUCT: ar.standardProducts,
  LOCAL_PRODUCT: ar.localProducts,
  WEIGHT_PRODUCT: ar.weightProducts,
  WEIGHT_DEFINED_PRODUCT: ar.weightDefinedProducts,
}

export default function Products() {
  const params = useLocalSearchParams<{ type: ProductType }>()
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebounce(search, 500)
  const state$ = useObservable({ search: false, filter: false })
  const type = params.type

  const numCols = Math.min(4, Math.max(2, useNumCols(250, { maxWidth: 1400 })))
  const products = trpc.products.list.useInfiniteQuery(
    { search: debouncedSearch, limit: 50, types: type ? [type] : undefined },
    { getNextPageParam: (lastPage) => lastPage.nextCursor },
  )

  const [showScanner, setShowScanner] = useState(false)

  function toggleSearch() {
    state$.set({ filter: false, search: !state$.search.get() })
    setSearch("")
  }

  function hideSearch() {
    state$.set({ filter: false, search: false })
    setSearch("")
  }

  function handleBarcodeSubmit(id: number) {
    router.push(`/dashboard/products/${id}`)
  }

  return (
    <>
      <Header
        title={TITLES[type]}
        right={
          <View className="flex-row h-full">
            <Pressable onPress={() => state$.set({ filter: true, search: false })} className="px-2 h-full justify-center active:opacity-70">
              <MCIcon name="filter" size={24} color={type ? "#60A5FA" : undefined} />
            </Pressable>

            <Pressable onPress={toggleSearch} className="px-2 h-full justify-center active:opacity-70">
              <MCIcon name="magnify" size={24} />
            </Pressable>

            <Link href="/dashboard/products/create" asChild>
              <Pressable className="px-2 h-full justify-center active:opacity-70">
                <MCIcon name="plus" size={24} />
              </Pressable>
            </Link>

            <Pressable onPress={() => setShowScanner(true)} className="px-2 h-full justify-center active:opacity-70">
              <MCIcon name="barcode" size={24} />
            </Pressable>
          </View>
        }
      />

      <View className="flex-1">
        <Show if={state$.search}>
          <View className="relative max-w-content mx-2 md:mx-auto md:w-full my-2">
            <TextInput value={search} onChangeText={setSearch} autoFocus />
            <Pressable className="absolute px-2 top-0 bottom-0 left-0 justify-center items-center active:opacity-70" onPress={hideSearch}>
              <MCIcon name="close" size={24} />
            </Pressable>
          </View>
        </Show>

        <List
          contentContainerStyle={sharedStyles.contentContainer}
          data={products.data?.pages.flatMap((page) => page.items) ?? []}
          numColumns={numCols}
          key={`storage-products-${numCols}`}
          keyExtractor={(item) => item.id.toString()}
          onEndReached={() => (products.hasNextPage ? products.fetchNextPage() : undefined)}
          onRefresh={products.refetch}
          onEndReachedThreshold={0.8}
          ListEmptyComponent={products.isLoading ? <ActivityIndicator className="py-8" /> : <Text className="text-gray-500 text-center py-8">{ar.noProducts}</Text>}
          renderItem={({ item }) => {
            const showOriginalPrice = item.originalPrice && item.originalPrice > item.price
            const image = item.images?.at(-1)

            return (
              <View className="bg-white p-2 rounded mb-2 mx-1 flex-1 border border-gray-200">
                <Link href={`/dashboard/products/${item.type === "WEIGHT_DEFINED_PRODUCT" ? item.baseProductId : item.id}`} asChild>
                  <Pressable className="active:opacity-70">
                    {image ? (
                      <Image
                        contentFit="contain"
                        cachePolicy="memory"
                        source={`${ENV.R2_URL}/${image.key}`}
                        className="w-full h-28 self-center mb-2"
                        recyclingKey={image.key}
                        transition={250}
                        loading="lazy"
                      />
                    ) : (
                      <View className="w-full h-28 bg-gray-100 rounded items-center justify-center mb-2">
                        <MCIcon name="image" size={40} color="#9CA3AF" />
                      </View>
                    )}

                    <Text className="text-sm font-semibold text-gray-900 text-center leading-tight mb-2" numberOfLines={2}>
                      {item.name}
                    </Text>

                    <Text className="text-xs text-gray-500 text-center mb-1 bg-gray-100 py-1">{item.upc}</Text>

                    <View className="items-center">
                      <Text className="text-base md:text-lg font-bold text-green-600">
                        {formatPrice(item.price)} {ar.egp}
                      </Text>
                      {showOriginalPrice ? (
                        <Text className="text-xs md:text-sm text-gray-500 line-through mt-0.5">
                          {formatPrice(item.originalPrice)} {ar.egp}
                        </Text>
                      ) : null}
                    </View>

                    {item.type === "WEIGHT_PRODUCT" || item.type === "WEIGHT_DEFINED_PRODUCT" ? (
                      <View className="absolute -top-1 -end-1 flex-row gap-1">
                        <View className="bg-green-400 px-2">
                          <Text className="text-white text-sm font-bold">{item.type === "WEIGHT_DEFINED_PRODUCT" ? formatWeight(item.value ?? 0, ar.kg, ar.gms) : ar.weight}</Text>
                        </View>
                        <View className="bg-blue-400 px-2">
                          <Text className="text-white text-sm font-bold">{getScaleCode(item.upc)}</Text>
                        </View>
                      </View>
                    ) : null}
                  </Pressable>
                </Link>
              </View>
            )
          }}
          ListFooterComponent={<SafeAreaView edges={["bottom"]} />}
        />
      </View>

      <Modal visible={showScanner} animationType="slide" onRequestClose={() => setShowScanner(false)}>
        <Scanner onSubmit={handleBarcodeSubmit} hide={() => setShowScanner(false)} />
      </Modal>

      <Show if={state$.filter}>
        <ModalWrapper onClose={() => state$.filter.set(false)}>
          <Text className="text-lg font-bold mb-2">{ar.productType}</Text>
          <View className="flex-row flex-wrap justify-between gap-y-2">
            <Pressable
              className={`w-[48%] rounded p-3 active:opacity-70 ${type === undefined ? "bg-blue-500" : "bg-gray-100"}`}
              onPress={() => {
                router.setParams({ type: undefined })
                state$.filter.set(false)
              }}
            >
              <Text className={`text-sm md:text-base font-bold text-center ${type === undefined ? "text-white" : "text-gray-700"}`}>{ar.all}</Text>
            </Pressable>

            <Pressable
              className={`w-[48%] rounded p-3 active:opacity-70 ${type === "STANDARD_PRODUCT" ? "bg-blue-500" : "bg-gray-100"}`}
              onPress={() => {
                router.setParams({ type: "STANDARD_PRODUCT" })
                state$.filter.set(false)
              }}
            >
              <Text className={`text-sm md:text-base font-bold text-center ${type === "STANDARD_PRODUCT" ? "text-white" : "text-gray-700"}`}>{ar.STANDARD_PRODUCT}</Text>
            </Pressable>

            <Pressable
              className={`w-[48%] rounded p-3 active:opacity-70 ${type === "LOCAL_PRODUCT" ? "bg-blue-500" : "bg-gray-100"}`}
              onPress={() => {
                router.setParams({ type: "LOCAL_PRODUCT" })
                state$.filter.set(false)
              }}
            >
              <Text className={`text-sm md:text-base font-bold text-center ${type === "LOCAL_PRODUCT" ? "text-white" : "text-gray-700"}`}>{ar.LOCAL_PRODUCT}</Text>
            </Pressable>

            <Pressable
              className={`w-[48%] rounded p-3 active:opacity-70 ${type === "WEIGHT_PRODUCT" ? "bg-blue-500" : "bg-gray-100"}`}
              onPress={() => {
                router.setParams({ type: "WEIGHT_PRODUCT" })
                state$.filter.set(false)
              }}
            >
              <Text className={`text-sm md:text-base font-bold text-center ${type === "WEIGHT_PRODUCT" ? "text-white" : "text-gray-700"}`}>{ar.WEIGHT_PRODUCT}</Text>
            </Pressable>

            <Pressable
              className={`w-[48%] rounded p-3 active:opacity-70 ${type === "WEIGHT_DEFINED_PRODUCT" ? "bg-blue-500" : "bg-gray-100"}`}
              onPress={() => {
                router.setParams({ type: "WEIGHT_DEFINED_PRODUCT" })
                state$.filter.set(false)
              }}
            >
              <Text className={`text-sm md:text-base font-bold text-center ${type === "WEIGHT_DEFINED_PRODUCT" ? "text-white" : "text-gray-700"}`}>
                {ar.WEIGHT_DEFINED_PRODUCT}
              </Text>
            </Pressable>
          </View>
        </ModalWrapper>
      </Show>
    </>
  )
}
